import moment, { isMoment } from 'moment-timezone';

const humanizeDuration = require('humanize-duration');

export const DISPLAY_DATE_FORMAT = 'MMM DD, YYYY, hh:mm:ss A z';
export const DISPLAY_DATE_FORMAT_WITHOUT_SEC = 'MMM DD, YYYY, hh:mm A z';
export const DISPLAY_DATE_FORMAT_WITHOUT_MIN = 'MMM DD, YYYY, hh A z';
export const DISPLAY_DATE_FORMAT_WITHOUT_TIME = 'MM-DD-YYYY';
export const DISPLAY_DATE_FORMAT_WITHOUT_COMMA = 'MMM DD, YYYY hh:mm A z';
export const DISPLAY_DATE_FORMAT_WITHOUT_COMMA_WITH_SEC = 'MMM DD, YYYY hh:mm:ss A z';
export const DISPLAY_DATE_FORMAT_ONLY_TIME = 'h:mm A z';
export const DATA_RETRIEVE_FORMAT = 'YYYY-MM-DD';
export const DISPLAY_MONTH_YEAR_FORMAT = 'MMM, YYYY';
export const PERFORMANCE_REPORT_DATE_FORMAT = 'MM/DD/YYYY';
export const PERFORMANCE_REPORT_TIME_FORMAT = 'hh:mm:ss A';

export const TIME_ZONE = moment.tz.guess();

export const convertDate = (date, seconds = true, mins = true) => {
    return !!date
        ? moment
              .utc(date)
              .tz(TIME_ZONE)
              .format(seconds ? DISPLAY_DATE_FORMAT : mins ? DISPLAY_DATE_FORMAT_WITHOUT_SEC : DISPLAY_DATE_FORMAT_WITHOUT_MIN)
        : '';
};

export const convertTicketDate = (date, seconds = true) => {
    return moment
        .utc(date)
        .tz(TIME_ZONE)
        .format(seconds ? DISPLAY_DATE_FORMAT_WITHOUT_COMMA_WITH_SEC : DISPLAY_DATE_FORMAT_WITHOUT_COMMA);
};

export const getDownloadFileDate = () => {
    return moment.utc(new Date()).local().format('MMDDYY_hhmmss');
};

export const convertDownloadDate = () => {
    return moment.utc(new Date()).local().format('YYYYMMDDhhmmss');
};

export const getSimpleDateFromMoment = (date, utc = true) => {
    if (isMoment(date)) {
        return utc ? moment(date).utc().format(DISPLAY_DATE_FORMAT_WITHOUT_TIME) : moment(date).format(DISPLAY_DATE_FORMAT_WITHOUT_TIME);
    }
    return date;
};

export const getHumanizeTime = (averageDuration, majorUnits = 2) => {
    const time = humanizeDuration(averageDuration, {
        round: true,
        delimiter: '##',
        units: ['y', 'mo', 'd', 'h', 'm', 's']
    });
    const tokens = time.split('##');
    let result = '';
    for (let index = 0; index < majorUnits; index++) {
        result += `${!!tokens[index] ? tokens[index] : ''} `;
    }
    return result.trim() === '0 seconds' || result.trim() === '0 milliseconds' ? '-' : result;
};

export const getFormattedDate = (date) => {
    return moment(date).format(DISPLAY_DATE_FORMAT_WITHOUT_TIME);
};

export const getThirtyDayDate = () => {
    return moment().subtract(30, 'days').format(DISPLAY_DATE_FORMAT_WITHOUT_TIME);
};

export const getNintyDayDate = () => {
    return moment().subtract(90, 'days').format(DISPLAY_DATE_FORMAT_WITHOUT_TIME);
};

export const getApiFormattedDate = (date) => {
    if (isMoment(date)) {
        return moment(date, DATA_RETRIEVE_FORMAT).format(DATA_RETRIEVE_FORMAT);
    } else if (typeof date === 'string') {
        const dateParts = date.split('-');
        return `${dateParts[2]}-${dateParts[0]}-${dateParts[1]}`;
    }
    return date;
};

export const getDateNoComma = (date) => {
    return moment(date).format(DISPLAY_DATE_FORMAT_WITHOUT_COMMA);
};

export const getDateOnlyTime = (date, isEpoch = false) => {
    if (isEpoch) {
        return moment.unix(date).format(DISPLAY_DATE_FORMAT_ONLY_TIME);
    } else {
        return moment(date).format(DISPLAY_DATE_FORMAT_ONLY_TIME);
    }
};

export const getLastNDay = (days = 0) => {
    let d = new Date();
    d.setDate(d.getDate() - days);
    return moment(d).format(DISPLAY_DATE_FORMAT_WITHOUT_TIME);
};

export const getLastNQuarter = (qtr = 0) => {
    let d = new Date();
    d.setDate(1);
    d.setMonth(d.getMonth() - qtr * 3);
    const year = d.getFullYear();
    const quarter = Math.ceil((d.getMonth() + 1) / 3);

    let start;
    let end;
    switch (quarter) {
        case 1:
            start = new Date(`01/01/${year}`);
            end = new Date(`03/31/${year}`);
            break;
        case 2:
            start = new Date(`04/01/${year}`);
            end = new Date(`06/30/${year}`);
            break;
        case 3:
            start = new Date(`07/01/${year}`);
            end = new Date(`09/30/${year}`);
            break;
        default:
            start = new Date(`10/01/${year}`);
            end = new Date(`12/31/${year}`);
    }

    return {
        value: `${start.toLocaleString('en-US', {
            month: 'short'
        })}-${end.toLocaleString('en-US', { month: 'short' })}, ${year}`,
        start: getFormattedDate(start),
        end: getFormattedDate(end)
    };
};

export const getLastNMonth = (month = 0) => {
    let d = new Date();
    d.setMonth(d.getMonth() - month);
    const year = d.getFullYear();

    let start = moment([year, d.getMonth()]);
    let end = moment(start).endOf('month');

    return {
        value: moment(d).format(DISPLAY_MONTH_YEAR_FORMAT),
        start: getFormattedDate(start),
        end: getFormattedDate(end)
    };
};

export const getMomentFormattedDate = (date) => {
    return date.format(moment.localeData().longDateFormat('L'));
};

// for use with the add/update user dropdown
export const timezones = [
    {
        key: '(GMT-12:00) International Date Line West',
        text: '(GMT-12:00) International Date Line West',
        value: 'Etc/GMT+12'
    },
    {
        key: '(GMT-11:00) Midway Island, Samoa',
        text: '(GMT-11:00) Midway Island, Samoa',
        value: 'Pacific/Midway'
    },
    {
        key: '(GMT-10:00) Hawaii',
        text: '(GMT-10:00) Hawaii',
        value: 'Pacific/Honolulu'
    },
    {
        key: '(GMT-09:00) Alaska',
        text: '(GMT-09:00) Alaska',
        value: 'US/Alaska'
    },
    {
        key: '(GMT-08:00) Pacific Time (US & Canada)',
        text: '(GMT-08:00) Pacific Time (US & Canada)',
        value: 'America/Los_Angeles'
    },
    {
        key: '(GMT-08:00) Tijuana, Baja California',
        text: '(GMT-08:00) Tijuana, Baja California',
        value: 'America/Tijuana'
    },
    {
        key: '(GMT-07:00) Arizona',
        text: '(GMT-07:00) Arizona',
        value: 'US/Arizona'
    },
    {
        key: '(GMT-07:00) Chihuahua, La Paz, Mazatlan',
        text: '(GMT-07:00) Chihuahua, La Paz, Mazatlan',
        value: 'America/Chihuahua'
    },
    {
        key: '(GMT-07:00) Mountain Time (US & Canada)',
        text: '(GMT-07:00) Mountain Time (US & Canada)',
        value: 'US/Mountain'
    },
    {
        key: '(GMT-06:00) Central America',
        text: '(GMT-06:00) Central America',
        value: 'America/Managua'
    },
    {
        key: '(GMT-06:00) Central Time (US & Canada)',
        text: '(GMT-06:00) Central Time (US & Canada)',
        value: 'US/Central'
    },
    {
        key: '(GMT-06:00) Guadalajara, Mexico City, Monterrey',
        text: '(GMT-06:00) Guadalajara, Mexico City, Monterrey',
        value: 'America/Mexico_City'
    },
    {
        key: '(GMT-06:00) Saskatchewan',
        text: '(GMT-06:00) Saskatchewan',
        value: 'Canada/Saskatchewan'
    },
    {
        key: '(GMT-05:00) Bogota, Lima, Quito, Rio Branco',
        text: '(GMT-05:00) Bogota, Lima, Quito, Rio Branco',
        value: 'America/Bogota'
    },
    {
        key: '(GMT-05:00) Eastern Time (US & Canada)',
        text: '(GMT-05:00) Eastern Time (US & Canada)',
        value: 'US/Eastern'
    },
    {
        key: '(GMT-05:00) Indiana (East)',
        text: '(GMT-05:00) Indiana (East)',
        value: 'US/East-Indiana'
    },
    {
        key: '(GMT-04:00) Atlantic Time (Canada)',
        text: '(GMT-04:00) Atlantic Time (Canada)',
        value: 'Canada/Atlantic'
    },
    {
        key: '(GMT-04:00) Caracas, La Paz',
        text: '(GMT-04:00) Caracas, La Paz',
        value: 'America/Caracas'
    },
    {
        key: '(GMT-04:00) Manaus',
        text: '(GMT-04:00) Manaus',
        value: 'America/Manaus'
    },
    {
        key: '(GMT-04:00) Santiago',
        text: '(GMT-04:00) Santiago',
        value: 'America/Santiago'
    },
    {
        key: '(GMT-03:30) Newfoundland',
        text: '(GMT-03:30) Newfoundland',
        value: 'Canada/Newfoundland'
    },
    {
        key: '(GMT-03:00) Brasilia',
        text: '(GMT-03:00) Brasilia',
        value: 'America/Sao_Paulo'
    },
    {
        key: '(GMT-03:00) Buenos Aires, Georgetown',
        text: '(GMT-03:00) Buenos Aires, Georgetown',
        value: 'America/Argentina/Buenos_Aires'
    },
    {
        key: '(GMT-03:00) Greenland',
        text: '(GMT-03:00) Greenland',
        value: 'America/Godthab'
    },
    {
        key: '(GMT-03:00) Montevideo',
        text: '(GMT-03:00) Montevideo',
        value: 'America/Montevideo'
    },
    {
        key: '(GMT-02:00) Mid-Atlantic',
        text: '(GMT-02:00) Mid-Atlantic',
        value: 'America/Noronha'
    },
    {
        key: '(GMT-01:00) Cape Verde Is.',
        text: '(GMT-01:00) Cape Verde Is.',
        value: 'Atlantic/Cape_Verde'
    },
    {
        key: '(GMT-01:00) Azores',
        text: '(GMT-01:00) Azores',
        value: 'Atlantic/Azores'
    },
    {
        key: '(GMT+00:00) Casablanca, Monrovia, Reykjavik',
        text: '(GMT+00:00) Casablanca, Monrovia, Reykjavik',
        value: 'Africa/Casablanca'
    },
    {
        key: '(GMT+00:00) Greenwich Mean Time : Dublin, Edinburgh, Lisbon, London',
        text: '(GMT+00:00) Greenwich Mean Time : Dublin, Edinburgh, Lisbon, London',
        value: 'Etc/Greenwich'
    },
    {
        key: '(GMT+01:00) Amsterdam, Berlin, Bern, Rome, Stockholm, Vienna',
        text: '(GMT+01:00) Amsterdam, Berlin, Bern, Rome, Stockholm, Vienna',
        value: 'Europe/Amsterdam'
    },
    {
        key: '(GMT+01:00) Belgrade, Bratislava, Budapest, Ljubljana, Prague',
        text: '(GMT+01:00) Belgrade, Bratislava, Budapest, Ljubljana, Prague',
        value: 'Europe/Belgrade'
    },
    {
        key: '(GMT+01:00) Brussels, Copenhagen, Madrid, Paris',
        text: '(GMT+01:00) Brussels, Copenhagen, Madrid, Paris',
        value: 'Europe/Brussels'
    },
    {
        key: '(GMT+01:00) Sarajevo, Skopje, Warsaw, Zagreb',
        text: '(GMT+01:00) Sarajevo, Skopje, Warsaw, Zagreb',
        value: 'Europe/Sarajevo'
    },
    {
        key: '(GMT+01:00) West Central Africa',
        text: '(GMT+01:00) West Central Africa',
        value: 'Africa/Lagos'
    },
    {
        key: '(GMT+02:00) Amman',
        text: '(GMT+02:00) Amman',
        value: 'Asia/Amman'
    },
    {
        key: '(GMT+02:00) Athens, Bucharest, Istanbul',
        text: '(GMT+02:00) Athens, Bucharest, Istanbul',
        value: 'Europe/Athens'
    },
    {
        key: '(GMT+02:00) Beirut',
        text: '(GMT+02:00) Beirut',
        value: 'Asia/Beirut'
    },
    {
        key: '(GMT+02:00) Cairo',
        text: '(GMT+02:00) Cairo',
        value: 'Africa/Cairo'
    },
    {
        key: '(GMT+02:00) Harare, Pretoria',
        text: '(GMT+02:00) Harare, Pretoria',
        value: 'Africa/Harare'
    },
    {
        key: '(GMT+02:00) Helsinki, Kyiv, Riga, Sofia, Tallinn, Vilnius',
        text: '(GMT+02:00) Helsinki, Kyiv, Riga, Sofia, Tallinn, Vilnius',
        value: 'Europe/Helsinki'
    },
    {
        key: '(GMT+02:00) Jerusalem',
        text: '(GMT+02:00) Jerusalem',
        value: 'Asia/Jerusalem'
    },
    {
        key: '(GMT+02:00) Minsk',
        text: '(GMT+02:00) Minsk',
        value: 'Europe/Minsk'
    },
    {
        key: '(GMT+02:00) Windhoek',
        text: '(GMT+02:00) Windhoek',
        value: 'Africa/Windhoek'
    },
    {
        key: '(GMT+03:00) Kuwait, Riyadh, Baghdad',
        text: '(GMT+03:00) Kuwait, Riyadh, Baghdad',
        value: 'Asia/Kuwait'
    },
    {
        key: '(GMT+03:00) Moscow, St. Petersburg, Volgograd',
        text: '(GMT+03:00) Moscow, St. Petersburg, Volgograd',
        value: 'Europe/Moscow'
    },
    {
        key: '(GMT+03:00) Nairobi',
        text: '(GMT+03:00) Nairobi',
        value: 'Africa/Nairobi'
    },
    {
        key: '(GMT+03:00) Tbilisi',
        text: '(GMT+03:00) Tbilisi',
        value: 'Asia/Tbilisi'
    },
    {
        key: '(GMT+03:30) Tehran',
        text: '(GMT+03:30) Tehran',
        value: 'Asia/Tehran'
    },
    {
        key: '(GMT+04:00) Abu Dhabi, Muscat',
        text: '(GMT+04:00) Abu Dhabi, Muscat',
        value: 'Asia/Muscat'
    },
    {
        key: '(GMT+04:00) Baku',
        text: '(GMT+04:00) Baku',
        value: 'Asia/Baku'
    },
    {
        key: '(GMT+04:00) Yerevan',
        text: '(GMT+04:00) Yerevan',
        value: 'Asia/Yerevan'
    },
    {
        key: '(GMT+04:30) Kabul',
        text: '(GMT+04:30) Kabul',
        value: 'Asia/Kabul'
    },
    {
        key: '(GMT+05:00) Yekaterinburg',
        text: '(GMT+05:00) Yekaterinburg',
        value: 'Asia/Yekaterinburg'
    },
    {
        key: '(GMT+05:00) Islamabad, Karachi, Tashkent',
        text: '(GMT+05:00) Islamabad, Karachi, Tashkent',
        value: 'Asia/Karachi'
    },
    {
        key: '(GMT+05:30) Chennai, Kolkata, Mumbai, New Delhi',
        text: '(GMT+05:30) Chennai, Kolkata, Mumbai, New Delhi',
        value: 'Asia/Calcutta'
    },
    {
        key: '(GMT+05:30) Sri Jayawardenapura',
        text: '(GMT+05:30) Sri Jayawardenapura',
        value: 'Asia/Calcutta'
    },
    {
        key: '(GMT+05:45) Kathmandu',
        text: '(GMT+05:45) Kathmandu',
        value: 'Asia/Katmandu'
    },
    {
        key: '(GMT+06:00) Almaty, Novosibirsk',
        text: '(GMT+06:00) Almaty, Novosibirsk',
        value: 'Asia/Almaty'
    },
    {
        key: '(GMT+06:00) Astana, Dhaka',
        text: '(GMT+06:00) Astana, Dhaka',
        value: 'Asia/Dhaka'
    },
    {
        key: '(GMT+06:30) Yangon (Rangoon)',
        text: '(GMT+06:30) Yangon (Rangoon)',
        value: 'Asia/Rangoon'
    },
    {
        key: '(GMT+07:00) Bangkok, Hanoi, Jakarta',
        text: '(GMT+07:00) Bangkok, Hanoi, Jakarta',
        value: 'Asia/Bangkok'
    },
    {
        key: '(GMT+07:00) Krasnoyarsk',
        text: '(GMT+07:00) Krasnoyarsk',
        value: 'Asia/Krasnoyarsk'
    },
    {
        key: '(GMT+08:00) Beijing, Chongqing, Hong Kong, Urumqi',
        text: '(GMT+08:00) Beijing, Chongqing, Hong Kong, Urumqi',
        value: 'Asia/Hong_Kong'
    },
    {
        key: '(GMT+08:00) Kuala Lumpur, Singapore',
        text: '(GMT+08:00) Kuala Lumpur, Singapore',
        value: 'Asia/Kuala_Lumpur'
    },
    {
        key: '(GMT+08:00) Irkutsk, Ulaan Bataar',
        text: '(GMT+08:00) Irkutsk, Ulaan Bataar',
        value: 'Asia/Irkutsk'
    },
    {
        key: '(GMT+08:00) Perth',
        text: '(GMT+08:00) Perth',
        value: 'Australia/Perth'
    },
    {
        key: '(GMT+08:00) Taipei',
        text: '(GMT+08:00) Taipei',
        value: 'Asia/Taipei'
    },
    {
        key: '(GMT+09:00) Osaka, Sapporo, Tokyo',
        text: '(GMT+09:00) Osaka, Sapporo, Tokyo',
        value: 'Asia/Tokyo'
    },
    {
        key: '(GMT+09:00) Seoul',
        text: '(GMT+09:00) Seoul',
        value: 'Asia/Seoul'
    },
    {
        key: '(GMT+09:00) Yakutsk',
        text: '(GMT+09:00) Yakutsk',
        value: 'Asia/Yakutsk'
    },
    {
        key: '(GMT+09:30) Adelaide',
        text: '(GMT+09:30) Adelaide',
        value: 'Australia/Adelaide'
    },
    {
        key: '(GMT+09:30) Darwin',
        text: '(GMT+09:30) Darwin',
        value: 'Australia/Darwin'
    },
    {
        key: '(GMT+10:00) Brisbane',
        text: '(GMT+10:00) Brisbane',
        value: 'Australia/Brisbane'
    },
    {
        key: '(GMT+10:00) Canberra, Melbourne, Sydney',
        text: '(GMT+10:00) Canberra, Melbourne, Sydney',
        value: 'Australia/Canberra'
    },
    {
        key: '(GMT+10:00) Hobart',
        text: '(GMT+10:00) Hobart',
        value: 'Australia/Hobart'
    },
    {
        key: '(GMT+10:00) Guam, Port Moresby',
        text: '(GMT+10:00) Guam, Port Moresby',
        value: 'Pacific/Guam'
    },
    {
        key: '(GMT+10:00) Vladivostok',
        text: '(GMT+10:00) Vladivostok',
        value: 'Asia/Vladivostok'
    },
    {
        key: '(GMT+11:00) Magadan, Solomon Is., New Caledonia',
        text: '(GMT+11:00) Magadan, Solomon Is., New Caledonia',
        value: 'Asia/Magadan'
    },
    {
        key: '(GMT+12:00) Auckland, Wellington',
        text: '(GMT+12:00) Auckland, Wellington',
        value: 'Pacific/Auckland'
    },
    {
        key: '(GMT+12:00) Fiji, Kamchatka, Marshall Is.',
        text: '(GMT+12:00) Fiji, Kamchatka, Marshall Is.',
        value: 'Pacific/Fiji'
    },
    {
        key: "(GMT+13:00) Nuku'alofa",
        text: "(GMT+13:00) Nuku'alofa",
        value: 'Pacific/Tongatapu'
    }
];

export const getDateRangeValidationError = (valueFromDate, valueToDate, utc = true) => {
    // Convert to timezone-aware moments in the user's timezone
    const fromMoment = moment(valueFromDate).tz(TIME_ZONE).startOf('day');
    const toMoment = moment(valueToDate).tz(TIME_ZONE).startOf('day');
    const nowMoment = moment().tz(TIME_ZONE).startOf('day');

    if (fromMoment.isAfter(toMoment)) {
        return 'From date should  be greater than To date';
    } else if (toMoment.isAfter(nowMoment) || fromMoment.isAfter(nowMoment)) {
        return 'To date cannot be future date.';
    }
    return '';
};

export const getCurrentDateWithTime = (date) => {
    const currentDateTime = moment();
    if (moment(moment(date).format('MM-DD-YYYY')).isSame(currentDateTime.format('MM-DD-YYYY'))) {
        const updatedDate = moment(date)
            .set('hour', currentDateTime.hours())
            .set('minute', currentDateTime.minutes())
            .set('second', currentDateTime.seconds())
            .set('millisecond', currentDateTime.milliseconds());
        return moment(moment(updatedDate).format('MM-DD-YYYY hh:mm:ss.SSS A'));
    }
    return date;
};
